import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Employee, EmployeeDataService } from '../../sifrarnik/services/employee-data.service';
import { PayrollDataService, Payroll } from '../../sifrarnik/services/payroll-data.service';
import { ClientService, GetClientQuery } from '../../invoices/services/client.service';

export interface SalaryCalculation {
  employeeId: string;
  employeeName: string;
  period: string;
  year: number;
  month: number;
  grossSalary: number;
  // Employee contributions (IZ plate)
  pioEmployee: number;
  healthEmployee: number;
  unemploymentEmployee: number;
  totalEmployeeContributions: number;
  // Income & Tax
  income: number;
  personalDeduction: number;
  taxBase: number;
  taxAmount: number;
  // Net
  netSalary: number;
  // Employer contributions (NA platu)
  pioEmployer: number;
  healthEmployer: number;
  unemploymentEmployer: number;
  totalEmployerContributions: number;
  // Totals per contribution type
  totalPio: number;
  totalHealth: number;
  totalUnemployment: number;
  totalContributions: number;
  // Total cost
  totalCost: number;
}

export interface CompanyPayrollSettings {
  companyName: string;
  tin: string;
  address: string;
  municipality: string;
  activityCode: string;
  activityName: string;
  bankAccount: string;
  bankName: string;
}

@Component({
  selector: 'app-salary-calculation-dialog',
  templateUrl: './salary-calculation-dialog.component.html',
  styleUrls: ['./salary-calculation-dialog.component.css']
})
export class SalaryCalculationDialogComponent implements OnInit {
  calculationForm: FormGroup;
  companySettingsForm: FormGroup;
  employees: Employee[] = [];
  selectedEmployees: Employee[] = [];
  calculations: SalaryCalculation[] = [];
  
  // Company settings for Form 2002
  companySettings: CompanyPayrollSettings | null = null;
  showCompanySettings = false;
  isSavingSettings = false;
  clientData: GetClientQuery | null = null;
  isGeneratingForm = false;
  isGeneratingSlips = false;

  // Lambda Function URL for payroll PDF generation
  private payrollPdfLambdaUrl = 'https://v7nape5sy7trnkrlcicnqisk340vkxoe.lambda-url.eu-central-1.on.aws/';

  // Default rates (FBiH - Bosnia and Herzegovina)
  defaultRates = {
    // Employee contributions (IZ plate)
    pioEmployee: 17,
    healthEmployee: 12.5,
    unemploymentEmployee: 1.5,
    // Employer contributions (NA platu)
    pioEmployer: 2.5,
    healthEmployer: 2,
    unemploymentEmployer: 0.5,
    // Tax
    tax: 10,
    personalDeduction: 300
  };

  months = [
    { value: 1, name: 'Januar' },
    { value: 2, name: 'Februar' },
    { value: 3, name: 'Mart' },
    { value: 4, name: 'April' },
    { value: 5, name: 'Maj' },
    { value: 6, name: 'Juni' },
    { value: 7, name: 'Juli' },
    { value: 8, name: 'August' },
    { value: 9, name: 'Septembar' },
    { value: 10, name: 'Oktobar' },
    { value: 11, name: 'Novembar' },
    { value: 12, name: 'Decembar' }
  ];

  years: number[] = [];
  isCalculated = false;
  isSaving = false;
  savedPayrolls: Payroll[] = [];
  selectedPayrollId: string | null = null;
  currentPayrollId: string | null = null;
  currentPayrollStatus: 'DRAFT' | 'CONFIRMED' | null = null;

  constructor(
    public dialogRef: MatDialogRef<SalaryCalculationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private employeeService: EmployeeDataService,
    private payrollService: PayrollDataService,
    private clientService: ClientService
  ) {
    this.calculationForm = this.createForm();
    this.companySettingsForm = this.createCompanySettingsForm();
    this.generateYears();
  }

  ngOnInit(): void {
    this.loadEmployees();
    this.loadSavedPayrolls();
    this.loadClientSettings();
    if (this.data?.payrollId) {
      this.loadPayroll(this.data.payrollId);
    }
  }

  createForm(): FormGroup {
    const currentDate = new Date();
    const defaultWorkingDays = this.getWorkingDaysInMonth(currentDate.getMonth() + 1, currentDate.getFullYear());
    return this.fb.group({
      selectedEmployeeIds: [[], Validators.required],
      month: [currentDate.getMonth() + 1, Validators.required],
      year: [currentDate.getFullYear(), Validators.required],
      workingDays: [defaultWorkingDays, [Validators.required, Validators.min(0)]],
      contributionPaymentDate: [null],
      // Employee contributions (IZ plate)
      pioEmployeeRate: [this.defaultRates.pioEmployee, [Validators.required, Validators.min(0)]],
      healthEmployeeRate: [this.defaultRates.healthEmployee, [Validators.required, Validators.min(0)]],
      unemploymentEmployeeRate: [this.defaultRates.unemploymentEmployee, [Validators.required, Validators.min(0)]],
      // Employer contributions (NA platu)
      pioEmployerRate: [this.defaultRates.pioEmployer, [Validators.required, Validators.min(0)]],
      healthEmployerRate: [this.defaultRates.healthEmployer, [Validators.required, Validators.min(0)]],
      unemploymentEmployerRate: [this.defaultRates.unemploymentEmployer, [Validators.required, Validators.min(0)]],
      // Tax
      taxRate: [this.defaultRates.tax, [Validators.required, Validators.min(0), Validators.max(100)]],
      personalDeduction: [this.defaultRates.personalDeduction, [Validators.required, Validators.min(0)]]
    });
  }

  createCompanySettingsForm(): FormGroup {
    return this.fb.group({
      companyName: ['', Validators.required],
      tin: ['', Validators.required],
      address: [''],
      municipality: [''],
      activityCode: [''],
      activityName: [''],
      bankAccount: [''],
      bankName: ['']
    });
  }

  getWorkingDaysInMonth(month: number, year: number): number {
    const daysInMonth = new Date(year, month, 0).getDate();
    let workingDays = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(year, month - 1, day);
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) {
        workingDays++;
      }
    }
    return workingDays;
  }

  generateYears(): void {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      this.years.push(i);
    }
  }

  async loadEmployees(): Promise<void> {
    const clientId = localStorage.getItem('userID');
    if (clientId) {
      try {
        const result = await this.employeeService.listEmployees(clientId);
        this.employees = result.items;
      } catch (error) {
        this.snackBar.open('Failed to load employees', 'Dismiss', {
          duration: 3000,
          panelClass: ['white-snackbar']
        });
      }
    }
  }

  selectAllEmployees(): void {
    const allIds = this.employees.map(e => e.id);
    this.calculationForm.patchValue({ selectedEmployeeIds: allIds });
  }

  clearSelection(): void {
    this.calculationForm.patchValue({ selectedEmployeeIds: [] });
  }

  calculate(): void {
    if (this.calculationForm.invalid) {
      this.snackBar.open('Please fill in all required fields', 'Dismiss', {
        duration: 3000,
        panelClass: ['white-snackbar']
      });
      return;
    }

    const formValue = this.calculationForm.value;
    const selectedIds = formValue.selectedEmployeeIds;
    
    this.selectedEmployees = this.employees.filter(e => selectedIds.includes(e.id));
    this.calculations = [];

    for (const employee of this.selectedEmployees) {
      const grossSalary = employee.grossSalary || 0;
      
      // Employee contributions (IZ plate)
      const pioEmployee = grossSalary * (formValue.pioEmployeeRate / 100);
      const healthEmployee = grossSalary * (formValue.healthEmployeeRate / 100);
      const unemploymentEmployee = grossSalary * (formValue.unemploymentEmployeeRate / 100);
      const totalEmployeeContributions = pioEmployee + healthEmployee + unemploymentEmployee;
      
      // Income and tax (FBiH formula)
      const income = grossSalary - totalEmployeeContributions;
      const personalDeduction = formValue.personalDeduction;
      const taxBase = Math.max(0, income - personalDeduction);
      const taxAmount = taxBase * (formValue.taxRate / 100);
      
      // Net salary
      const netSalary = income - taxAmount;
      
      // Employer contributions (NA platu)
      const pioEmployer = grossSalary * (formValue.pioEmployerRate / 100);
      const healthEmployer = grossSalary * (formValue.healthEmployerRate / 100);
      const unemploymentEmployer = grossSalary * (formValue.unemploymentEmployerRate / 100);
      const totalEmployerContributions = pioEmployer + healthEmployer + unemploymentEmployer;
      
      // Totals
      const totalCost = grossSalary + totalEmployerContributions + totalEmployeeContributions;

      this.calculations.push({
        employeeId: employee.id,
        employeeName: employee.fullName || `${employee.firstName} ${employee.lastName}`,
        period: `${this.months.find(m => m.value === formValue.month)?.name} ${formValue.year}`,
        year: formValue.year,
        month: formValue.month,
        grossSalary: this.roundToTwo(grossSalary),
        pioEmployee: this.roundToTwo(pioEmployee),
        healthEmployee: this.roundToTwo(healthEmployee),
        unemploymentEmployee: this.roundToTwo(unemploymentEmployee),
        totalEmployeeContributions: this.roundToTwo(totalEmployeeContributions),
        income: this.roundToTwo(income),
        personalDeduction: this.roundToTwo(personalDeduction),
        taxBase: this.roundToTwo(taxBase),
        taxAmount: this.roundToTwo(taxAmount),
        netSalary: this.roundToTwo(netSalary),
        pioEmployer: this.roundToTwo(pioEmployer),
        healthEmployer: this.roundToTwo(healthEmployer),
        unemploymentEmployer: this.roundToTwo(unemploymentEmployer),
        totalEmployerContributions: this.roundToTwo(totalEmployerContributions),
        totalPio: this.roundToTwo(pioEmployee + pioEmployer),
        totalHealth: this.roundToTwo(healthEmployee + healthEmployer),
        totalUnemployment: this.roundToTwo(unemploymentEmployee + unemploymentEmployer),
        totalContributions: this.roundToTwo(totalEmployeeContributions + totalEmployerContributions),
        totalCost: this.roundToTwo(totalCost)
      });
    }

    this.isCalculated = true;
  }

  roundToTwo(num: number): number {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  }

  getTotals(): any {
    return {
      grossSalary: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.grossSalary, 0)),
      pioEmployee: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.pioEmployee, 0)),
      healthEmployee: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.healthEmployee, 0)),
      unemploymentEmployee: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.unemploymentEmployee, 0)),
      totalEmployeeContributions: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.totalEmployeeContributions, 0)),
      income: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.income, 0)),
      taxAmount: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.taxAmount, 0)),
      netSalary: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.netSalary, 0)),
      pioEmployer: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.pioEmployer, 0)),
      healthEmployer: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.healthEmployer, 0)),
      unemploymentEmployer: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.unemploymentEmployer, 0)),
      totalEmployerContributions: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.totalEmployerContributions, 0)),
      totalPio: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.totalPio, 0)),
      totalHealth: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.totalHealth, 0)),
      totalUnemployment: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.totalUnemployment, 0)),
      totalContributions: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.totalContributions, 0)),
      totalCost: this.roundToTwo(this.calculations.reduce((sum, c) => sum + c.totalCost, 0))
    };
  }

  exportTxt(): void {
    const totals = this.getTotals();
    const formValue = this.calculationForm.value;
    const periodName = `${this.months.find(m => m.value === formValue.month)?.name} ${formValue.year}`;
    
    let content = `OBRAČUN PLAĆA - ${periodName}\n`;
    content += '='.repeat(80) + '\n\n';
    
    for (const calc of this.calculations) {
      content += `Zaposlenik: ${calc.employeeName}\n`;
      content += `-`.repeat(60) + '\n';
      content += `Bruto plaća (Osnovica):         ${this.formatCurrency(calc.grossSalary)}\n`;
      content += `\n  Doprinosi IZ plate:\n`;
      content += `    PIO (${this.calculationForm.value.pioEmployeeRate}%):                   ${this.formatCurrency(calc.pioEmployee)}\n`;
      content += `    Zdravstvo (${this.calculationForm.value.healthEmployeeRate}%):             ${this.formatCurrency(calc.healthEmployee)}\n`;
      content += `    Nezaposlenost (${this.calculationForm.value.unemploymentEmployeeRate}%):        ${this.formatCurrency(calc.unemploymentEmployee)}\n`;
      content += `    UKUPNO IZ:                    ${this.formatCurrency(calc.totalEmployeeContributions)}\n`;
      content += `\n  Dohodak:                        ${this.formatCurrency(calc.income)}\n`;
      content += `  Lični odbitak:                  ${this.formatCurrency(calc.personalDeduction)}\n`;
      content += `  Porezna osnovica:               ${this.formatCurrency(calc.taxBase)}\n`;
      content += `  Porez na dohodak (${this.calculationForm.value.taxRate}%):       ${this.formatCurrency(calc.taxAmount)}\n`;
      content += `  NETO PLAĆA:                     ${this.formatCurrency(calc.netSalary)}\n`;
      content += `\n  Doprinosi NA platu:\n`;
      content += `    PIO (${this.calculationForm.value.pioEmployerRate}%):                    ${this.formatCurrency(calc.pioEmployer)}\n`;
      content += `    Zdravstvo (${this.calculationForm.value.healthEmployerRate}%):              ${this.formatCurrency(calc.healthEmployer)}\n`;
      content += `    Nezaposlenost (${this.calculationForm.value.unemploymentEmployerRate}%):         ${this.formatCurrency(calc.unemploymentEmployer)}\n`;
      content += `    UKUPNO NA:                    ${this.formatCurrency(calc.totalEmployerContributions)}\n`;
      content += `\n  UKUPNI TROŠAK:                  ${this.formatCurrency(calc.totalCost)}\n\n`;
    }
    
    content += '='.repeat(80) + '\n';
    content += 'UKUPNO:\n';
    content += `Bruto plaće:                    ${this.formatCurrency(totals.grossSalary)}\n`;
    content += `Doprinosi IZ plate:             ${this.formatCurrency(totals.totalEmployeeContributions)}\n`;
    content += `Porez na dohodak:               ${this.formatCurrency(totals.taxAmount)}\n`;
    content += `Neto plaće:                     ${this.formatCurrency(totals.netSalary)}\n`;
    content += `Doprinosi NA platu:             ${this.formatCurrency(totals.totalEmployerContributions)}\n`;
    content += `UKUPNI TROŠAK PLAĆA:            ${this.formatCurrency(totals.totalCost)}\n`;
    
    this.downloadFile(content, `obracun_placa_${formValue.month}_${formValue.year}.txt`, 'text/plain');
  }

  exportPdf(): void {
    // For PDF export, we'll generate HTML and use the browser's print functionality
    const totals = this.getTotals();
    const formValue = this.calculationForm.value;
    const periodName = `${this.months.find(m => m.value === formValue.month)?.name} ${formValue.year}`;
    
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Obračun plaća - ${periodName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          h1 { text-align: center; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: right; }
          th { background-color: #27272c; color: white; font-size: 11px; }
          .employee-name { text-align: left; font-weight: bold; }
          .total-row { background-color: #f5f5f5; font-weight: bold; }
          .grand-total { background-color: #27272c; color: white; }
          .section-header { background-color: #f0f0f0; font-weight: bold; text-align: center; }
        </style>
      </head>
      <body>
        <h1>OBRAČUN PLAĆA</h1>
        <h2 style="text-align: center;">${periodName}</h2>
        <table>
          <thead>
            <tr>
              <th style="text-align: left;">Zaposlenik</th>
              <th>Bruto I</th>
              <th>PIO IZ</th>
              <th>Zdrav. IZ</th>
              <th>Nezap. IZ</th>
              <th>Ukupno IZ</th>
              <th>Dohodak</th>
              <th>Porez</th>
              <th>Neto</th>
              <th>PIO NA</th>
              <th>Zdrav. NA</th>
              <th>Nezap. NA</th>
              <th>Ukupno NA</th>
              <th>Uk. trošak</th>
            </tr>
          </thead>
          <tbody>
    `;
    
    for (const calc of this.calculations) {
      html += `
        <tr>
          <td class="employee-name">${calc.employeeName}</td>
          <td>${this.formatCurrency(calc.grossSalary)}</td>
          <td>${this.formatCurrency(calc.pioEmployee)}</td>
          <td>${this.formatCurrency(calc.healthEmployee)}</td>
          <td>${this.formatCurrency(calc.unemploymentEmployee)}</td>
          <td>${this.formatCurrency(calc.totalEmployeeContributions)}</td>
          <td>${this.formatCurrency(calc.income)}</td>
          <td>${this.formatCurrency(calc.taxAmount)}</td>
          <td>${this.formatCurrency(calc.netSalary)}</td>
          <td>${this.formatCurrency(calc.pioEmployer)}</td>
          <td>${this.formatCurrency(calc.healthEmployer)}</td>
          <td>${this.formatCurrency(calc.unemploymentEmployer)}</td>
          <td>${this.formatCurrency(calc.totalEmployerContributions)}</td>
          <td>${this.formatCurrency(calc.totalCost)}</td>
        </tr>
      `;
    }
    
    html += `
          <tr class="grand-total">
            <td style="text-align: left;">UKUPNO</td>
            <td>${this.formatCurrency(totals.grossSalary)}</td>
            <td>${this.formatCurrency(totals.pioEmployee)}</td>
            <td>${this.formatCurrency(totals.healthEmployee)}</td>
            <td>${this.formatCurrency(totals.unemploymentEmployee)}</td>
            <td>${this.formatCurrency(totals.totalEmployeeContributions)}</td>
            <td>${this.formatCurrency(totals.income)}</td>
            <td>${this.formatCurrency(totals.taxAmount)}</td>
            <td>${this.formatCurrency(totals.netSalary)}</td>
            <td>${this.formatCurrency(totals.pioEmployer)}</td>
            <td>${this.formatCurrency(totals.healthEmployer)}</td>
            <td>${this.formatCurrency(totals.unemploymentEmployer)}</td>
            <td>${this.formatCurrency(totals.totalEmployerContributions)}</td>
            <td>${this.formatCurrency(totals.totalCost)}</td>
          </tr>
        </tbody>
      </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.print();
    }
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('bs-BA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value) + ' BAM';
  }

  downloadFile(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  async loadSavedPayrolls(): Promise<void> {
    const clientId = localStorage.getItem('userID');
    if (clientId) {
      try {
        const result = await this.payrollService.listPayrolls(clientId);
        this.savedPayrolls = result.items;
      } catch (error) {
        console.error('Error loading payrolls:', error);
      }
    }
  }

  async loadPayroll(payrollId: string): Promise<void> {
    try {
      const payroll = await this.payrollService.getPayroll(payrollId);
      this.currentPayrollId = payroll.id;
      this.currentPayrollStatus = payroll.status;
      const savedRates = payroll.rates
        ? (typeof payroll.rates === 'string' ? JSON.parse(payroll.rates) : payroll.rates)
        : null;

      this.calculationForm.patchValue({
        month: payroll.month,
        year: payroll.year,
        workingDays: payroll.workingDays || this.getWorkingDaysInMonth(payroll.month, payroll.year),
        contributionPaymentDate: payroll.contributionPaymentDate || null,
        ...(savedRates ? {
          pioEmployeeRate: savedRates.pioEmployeeRate,
          healthEmployeeRate: savedRates.healthEmployeeRate,
          unemploymentEmployeeRate: savedRates.unemploymentEmployeeRate,
          pioEmployerRate: savedRates.pioEmployerRate,
          healthEmployerRate: savedRates.healthEmployerRate,
          unemploymentEmployerRate: savedRates.unemploymentEmployerRate,
          taxRate: savedRates.taxRate,
          personalDeduction: savedRates.personalDeduction
        } : {})
      });

      this.calculations = (payroll.items || []).map((item: any) => ({
        employeeId: item.employeeId,
        employeeName: item.employeeName,
        period: `${this.months.find(m => m.value === payroll.month)?.name} ${payroll.year}`,
        year: payroll.year,
        month: payroll.month,
        grossSalary: item.grossSalary || 0,
        pioEmployee: item.pioEmployee || item.pensionInsurance || 0,
        healthEmployee: item.healthEmployee || item.healthInsurance || 0,
        unemploymentEmployee: item.unemploymentEmployee || item.unemploymentInsurance || 0,
        totalEmployeeContributions: item.totalEmployeeContributions || item.totalContributions || 0,
        income: item.income || (item.grossSalary - (item.totalEmployeeContributions || item.totalContributions || 0)),
        personalDeduction: item.personalDeduction || 300,
        taxBase: item.taxBase || 0,
        taxAmount: item.taxAmount || item.tax || 0,
        netSalary: item.netSalary || 0,
        pioEmployer: item.pioEmployer || 0,
        healthEmployer: item.healthEmployer || 0,
        unemploymentEmployer: item.unemploymentEmployer || 0,
        totalEmployerContributions: item.totalEmployerContributions || item.employerContributions || 0,
        totalPio: (item.pioEmployee || item.pensionInsurance || 0) + (item.pioEmployer || 0),
        totalHealth: (item.healthEmployee || item.healthInsurance || 0) + (item.healthEmployer || 0),
        totalUnemployment: (item.unemploymentEmployee || item.unemploymentInsurance || 0) + (item.unemploymentEmployer || 0),
        totalContributions: (item.totalEmployeeContributions || item.totalContributions || 0) + (item.totalEmployerContributions || item.employerContributions || 0),
        totalCost: item.totalCost || (item.grossSalary + (item.totalEmployerContributions || item.employerContributions || 0))
      }));

      this.isCalculated = true;
      this.snackBar.open('Payroll loaded', 'Dismiss', {
        duration: 2000,
        panelClass: ['white-snackbar']
      });
    } catch (error) {
      this.snackBar.open('Failed to load payroll', 'Dismiss', {
        duration: 3000,
        panelClass: ['white-snackbar']
      });
    }
  }

  async savePayroll(): Promise<void> {
    if (!this.isCalculated || this.calculations.length === 0) return;
    this.isSaving = true;

    const formValue = this.calculationForm.value;
    const totals = this.getTotals();
    const clientID = localStorage.getItem('userID');
    if (!clientID) { this.isSaving = false; return; }

    const items = this.calculations.map(c => ({
      employeeId: c.employeeId,
      employeeName: c.employeeName,
      grossSalary: c.grossSalary,
      netSalary: c.netSalary,
      taxAmount: c.taxAmount,
      pioEmployee: c.pioEmployee,
      healthEmployee: c.healthEmployee,
      unemploymentEmployee: c.unemploymentEmployee,
      totalEmployeeContributions: c.totalEmployeeContributions,
      income: c.income,
      personalDeduction: c.personalDeduction,
      taxBase: c.taxBase,
      pioEmployer: c.pioEmployer,
      healthEmployer: c.healthEmployer,
      unemploymentEmployer: c.unemploymentEmployer,
      totalEmployerContributions: c.totalEmployerContributions,
      totalCost: c.totalCost
    }));

    try {
      if (this.currentPayrollId) {
        await this.payrollService.updatePayroll({
          id: this.currentPayrollId,
          month: formValue.month,
          year: formValue.year,
          items: items as any,
          totalGross: totals.grossSalary,
          totalNet: totals.netSalary,
          totalTax: totals.taxAmount,
          totalContributions: totals.totalContributions,
          workingDays: formValue.workingDays,
          contributionPaymentDate: formValue.contributionPaymentDate || undefined,
          rates: {
            pioEmployeeRate: formValue.pioEmployeeRate,
            healthEmployeeRate: formValue.healthEmployeeRate,
            unemploymentEmployeeRate: formValue.unemploymentEmployeeRate,
            pioEmployerRate: formValue.pioEmployerRate,
            healthEmployerRate: formValue.healthEmployerRate,
            unemploymentEmployerRate: formValue.unemploymentEmployerRate,
            taxRate: formValue.taxRate,
            personalDeduction: formValue.personalDeduction
          }
        });
        this.snackBar.open('Payroll updated', 'Dismiss', { duration: 2000, panelClass: ['white-snackbar'] });
      } else {
        const newPayroll = await this.payrollService.createPayroll({
          month: formValue.month,
          year: formValue.year,
          items: items as any,
          totalGross: totals.grossSalary,
          totalNet: totals.netSalary,
          totalTax: totals.taxAmount,
          totalContributions: totals.totalContributions,
          workingDays: formValue.workingDays,
          contributionPaymentDate: formValue.contributionPaymentDate || undefined,
          rates: {
            pioEmployeeRate: formValue.pioEmployeeRate,
            healthEmployeeRate: formValue.healthEmployeeRate,
            unemploymentEmployeeRate: formValue.unemploymentEmployeeRate,
            pioEmployerRate: formValue.pioEmployerRate,
            healthEmployerRate: formValue.healthEmployerRate,
            unemploymentEmployerRate: formValue.unemploymentEmployerRate,
            taxRate: formValue.taxRate,
            personalDeduction: formValue.personalDeduction
          },
          clientID
        });
        this.currentPayrollId = newPayroll.id;
        this.currentPayrollStatus = 'DRAFT';
        this.snackBar.open('Payroll saved as draft', 'Dismiss', { duration: 2000, panelClass: ['white-snackbar'] });
      }
      await this.loadSavedPayrolls();
    } catch (error) {
      this.snackBar.open('Failed to save payroll', 'Dismiss', { duration: 3000, panelClass: ['white-snackbar'] });
    }
    this.isSaving = false;
  }

  async confirmPayroll(): Promise<void> {
    if (!this.currentPayrollId) {
      await this.savePayroll();
    }
    if (!this.currentPayrollId) return;

    try {
      await this.payrollService.confirmPayroll(this.currentPayrollId);
      this.currentPayrollStatus = 'CONFIRMED';
      await this.loadSavedPayrolls();
      this.snackBar.open('Payroll confirmed', 'Dismiss', { duration: 2000, panelClass: ['white-snackbar'] });
    } catch (error) {
      this.snackBar.open('Failed to confirm payroll', 'Dismiss', { duration: 3000, panelClass: ['white-snackbar'] });
    }
  }

  getPayrollLabel(payroll: Payroll): string {
    const monthName = this.months.find(m => m.value === payroll.month)?.name || '';
    return `${monthName} ${payroll.year} (${payroll.status})`;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  onMonthYearChange(): void {
    const formValue = this.calculationForm.value;
    if (formValue.month && formValue.year) {
      const workingDays = this.getWorkingDaysInMonth(formValue.month, formValue.year);
      this.calculationForm.patchValue({ workingDays });
    }
  }

  toggleCompanySettings(): void {
    this.showCompanySettings = !this.showCompanySettings;
  }

  async loadClientSettings(): Promise<void> {
    const clientId = localStorage.getItem('userID');
    if (!clientId) return;

    try {
      this.clientData = await this.clientService.GetClient(clientId);
      if (this.clientData) {
        let settings: any = null;
        if (this.clientData.payrollSettings) {
          try {
            settings = JSON.parse(this.clientData.payrollSettings);
          } catch (e) { /* ignore parse errors */ }
        }

        this.companySettings = {
          companyName: settings?.companyName || this.clientData.company || '',
          tin: settings?.tin || this.clientData.tin || '',
          address: settings?.address || this.clientData.address || '',
          municipality: settings?.municipality || this.clientData.municipality || '',
          activityCode: settings?.activityCode || this.clientData.activityCode || '',
          activityName: settings?.activityName || this.clientData.activityName || '',
          bankAccount: settings?.bankAccount || this.clientData.bankAccount || '',
          bankName: settings?.bankName || this.clientData.bankName || ''
        };

        this.companySettingsForm.patchValue(this.companySettings);

        // Auto-show settings if not yet configured
        if (!this.companySettings.tin) {
          this.showCompanySettings = true;
        }
      }
    } catch (error) {
      console.error('Error loading client settings:', error);
    }
  }

  async saveClientSettings(): Promise<void> {
    if (this.companySettingsForm.invalid) return;
    this.isSavingSettings = true;
    const clientId = localStorage.getItem('userID');
    if (!clientId) { this.isSavingSettings = false; return; }

    const formValue = this.companySettingsForm.value;
    this.companySettings = formValue;

    try {
      await this.clientService.UpdateClient({
        id: clientId,
        tin: formValue.tin,
        municipality: formValue.municipality,
        activityCode: formValue.activityCode,
        activityName: formValue.activityName,
        bankAccount: formValue.bankAccount,
        bankName: formValue.bankName,
        payrollSettings: JSON.stringify(formValue)
      });

      this.snackBar.open('Postavke kompanije sačuvane', 'Dismiss', {
        duration: 2000,
        panelClass: ['white-snackbar']
      });
      this.showCompanySettings = false;
    } catch (error) {
      this.snackBar.open('Greška pri čuvanju postavki', 'Dismiss', {
        duration: 3000,
        panelClass: ['white-snackbar']
      });
    }
    this.isSavingSettings = false;
  }

  async generateForm2002(): Promise<void> {
    if (!this.isCalculated || this.calculations.length === 0) {
      this.snackBar.open('Prvo izvršite obračun plaća', 'Dismiss', {
        duration: 3000, panelClass: ['white-snackbar']
      });
      return;
    }

    const settings = this.companySettings;
    if (!settings || !settings.tin) {
      this.snackBar.open('Molimo popunite postavke kompanije (TIN/JMB)', 'Dismiss', {
        duration: 3000, panelClass: ['white-snackbar']
      });
      this.showCompanySettings = true;
      return;
    }

    if (!this.payrollPdfLambdaUrl) {
      this.snackBar.open('Lambda URL nije konfigurisan', 'Dismiss', {
        duration: 3000, panelClass: ['white-snackbar']
      });
      return;
    }

    this.isGeneratingForm = true;
    const formValue = this.calculationForm.value;
    const clientId = localStorage.getItem('userID') || '';

    const employeesPayload = this.calculations.map(calc => {
      const emp = this.employees.find(e => e.id === calc.employeeId);
      return {
        employeeName: calc.employeeName,
        jmbg: emp?.jmbg || '',
        address: emp?.address || '',
        municipality: emp?.residence || emp?.municipalityCode || '',
        grossSalary: calc.grossSalary,
        totalPio: calc.totalPio,
        totalHealth: calc.totalHealth,
        totalUnemployment: calc.totalUnemployment,
        totalContributions: calc.totalContributions,
      };
    });

    const payload = {
      operation: 'GENERATE_FORM_2002',
      clientId,
      company: settings,
      payroll: {
        month: formValue.month,
        year: formValue.year,
        workingDays: formValue.workingDays,
        contributionPaymentDate: formValue.contributionPaymentDate || '',
        rates: {
          pioEmployeeRate: formValue.pioEmployeeRate,
          healthEmployeeRate: formValue.healthEmployeeRate,
          unemploymentEmployeeRate: formValue.unemploymentEmployeeRate,
          pioEmployerRate: formValue.pioEmployerRate,
          healthEmployerRate: formValue.healthEmployerRate,
          unemploymentEmployerRate: formValue.unemploymentEmployerRate,
        }
      },
      employees: employeesPayload,
    };

    try {
      const result = await this.callPayrollLambda(payload);
      if (result.success && result.pdfUrl) {
        window.open(result.pdfUrl, '_blank');
        this.snackBar.open('Obrazac 2002 generisan', 'Dismiss', {
          duration: 2000, panelClass: ['white-snackbar']
        });
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (error: any) {
      console.error('Error generating Form 2002:', error);
      this.snackBar.open(`Greška: ${error.message || 'Nije moguće generisati obrazac'}`, 'Dismiss', {
        duration: 4000, panelClass: ['white-snackbar']
      });
    }
    this.isGeneratingForm = false;
  }

  async generatePaymentSlips(): Promise<void> {
    if (!this.isCalculated || this.calculations.length === 0) {
      this.snackBar.open('Prvo izvršite obračun plaća', 'Dismiss', {
        duration: 3000, panelClass: ['white-snackbar']
      });
      return;
    }

    const settings = this.companySettings;
    if (!settings || !settings.tin) {
      this.snackBar.open('Molimo popunite postavke kompanije (TIN/JMB)', 'Dismiss', {
        duration: 3000, panelClass: ['white-snackbar']
      });
      this.showCompanySettings = true;
      return;
    }

    if (!this.payrollPdfLambdaUrl) {
      this.snackBar.open('Lambda URL nije konfigurisan', 'Dismiss', {
        duration: 3000, panelClass: ['white-snackbar']
      });
      return;
    }

    this.isGeneratingSlips = true;
    const formValue = this.calculationForm.value;
    const clientId = localStorage.getItem('userID') || '';
    const totals = this.getTotals();

    const items = this.calculations.map(calc => ({
      employeeName: calc.employeeName,
      netSalary: calc.netSalary,
      bankAccount: '', // TODO: populate from employee bank account if available
    }));

    const payload = {
      operation: 'GENERATE_PAYMENT_SLIPS',
      clientId,
      company: settings,
      payroll: {
        month: formValue.month,
        year: formValue.year,
        contributionPaymentDate: formValue.contributionPaymentDate || '',
        totals: {
          totalGross: totals.grossSalary,
          totalNet: totals.netSalary,
          totalTax: totals.taxAmount,
          totalPioEmployee: totals.pioEmployee,
          totalPioEmployer: totals.pioEmployer,
          totalPio: totals.totalPio,
          totalHealthEmployee: totals.healthEmployee,
          totalHealthEmployer: totals.healthEmployer,
          totalHealth: totals.totalHealth,
          totalUnemploymentEmployee: totals.unemploymentEmployee,
          totalUnemploymentEmployer: totals.unemploymentEmployer,
          totalUnemployment: totals.totalUnemployment,
          totalContributions: totals.totalContributions,
        },
        items,
      },
    };

    try {
      const result = await this.callPayrollLambda(payload);
      if (result.success && result.pdfUrl) {
        window.open(result.pdfUrl, '_blank');
        this.snackBar.open('Virmani generisani', 'Dismiss', {
          duration: 2000, panelClass: ['white-snackbar']
        });
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (error: any) {
      console.error('Error generating payment slips:', error);
      this.snackBar.open(`Greška: ${error.message || 'Nije moguće generisati virmane'}`, 'Dismiss', {
        duration: 4000, panelClass: ['white-snackbar']
      });
    }
    this.isGeneratingSlips = false;
  }

  private async callPayrollLambda(payload: any): Promise<any> {
    const response = await fetch(this.payrollPdfLambdaUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error || `HTTP ${response.status}`);
    }
    // Lambda Function URL wraps body as string
    if (typeof data.body === 'string') {
      return JSON.parse(data.body);
    }
    return data;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  }
}
